import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check active session with error handling
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase?.auth?.getSession();
        
        if (error) {
          console.error('Session error:', error);
          // Clear invalid session
          await supabase?.auth?.signOut();
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }

        setUser(session?.user ?? null);
        if (session?.user) {
          await loadUserProfile(session?.user?.id);
        } else {
          setLoading(false);
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        setUser(null);
        setProfile(null);
        setLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth changes with enhanced error handling
    const {
      data: { subscription },
    } = supabase?.auth?.onAuthStateChange(async (event, session) => {
      console.log('Auth state change:', event);

      // Handle email confirmation events
      if (event === 'SIGNED_IN' && session?.user) {
        console.log('User signed in:', session?.user?.email);
        setUser(session?.user);
        await loadUserProfile(session?.user?.id);
        return;
      }

      // Handle token refresh
      if (event === 'TOKEN_REFRESHED') {
        console.log('Token refreshed successfully');
        setUser(session?.user ?? null);
        if (session?.user) {
          await loadUserProfile(session?.user?.id);
        }
        return;
      }

      // Handle sign out
      if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
        setUser(null);
        setProfile(null);
        setLoading(false);
        return;
      }

      // Handle session errors - clear invalid sessions
      if (!session && (event === 'TOKEN_REFRESH_FAILED' || event === 'SIGNED_OUT')) {
        console.warn('Session invalid or expired - clearing auth state');
        setUser(null);
        setProfile(null);
        setLoading(false);
        // Silently sign out to clear any remaining tokens
        try {
          await supabase?.auth?.signOut();
        } catch (err) {
          console.error('Error signing out:', err);
        }
        return;
      }

      // Default: Update user state for valid sessions
      setUser(session?.user ?? null);
      if (session?.user) {
        await loadUserProfile(session?.user?.id);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription?.unsubscribe();
  }, []);

  const loadUserProfile = async (userId) => {
    try {
      setLoading(true);
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

      if (error) {
        // If profile doesn't exist, that's okay - user might not have completed profile
        if (error?.code !== 'PGRST116') {
          console.error('Error loading profile:', error);
        }
        setProfile(null);
      } else {
        console.log('User profile loaded successfully:', data?.role); // Debug log
        setProfile(data);
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email, password, fullName, role = 'employee') => {
    // Get the current window origin, with fallback for production
    const redirectUrl = window?.location?.origin || 'https://your-production-url.com';
    
    const { data, error } = await supabase?.auth?.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          role: role,
        },
        emailRedirectTo: `${redirectUrl}/auth/confirm`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email, password) => {
    try {
      // Clear any error state
      setLoading(true);
      
      // Validate inputs
      if (!email || !password) {
        throw new Error('Email and password are required');
      }

      // First, try to sign out any existing invalid session
      try {
        await supabase?.auth?.signOut();
      } catch (signOutErr) {
        // Ignore sign out errors - we're trying to sign in anyway
        console.log('Cleared previous session');
      }

      const { data, error } = await supabase?.auth?.signInWithPassword({
        email: email?.trim(),
        password: password,
      });

      if (error) {
        // Provide user-friendly error messages
        if (error?.message?.includes('Invalid login credentials')) {
          throw new Error('Incorrect email or password. Please try again.');
        } else if (error?.message?.includes('Email not confirmed')) {
          throw new Error('Please verify your email address before signing in.');
        } else {
          throw new Error(error.message);
        }
      }

      if (!data?.user) {
        throw new Error('Sign in failed. Please try again.');
      }

      // CRITICAL: Load user profile and WAIT for it to complete before returning
      // This ensures the profile role is available for routing decisions
      await loadUserProfile(data?.user?.id);
      
      // Get the freshly loaded profile from state
      const { data: profileData, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.eq('id', data?.user?.id)
        ?.single();

      if (profileError && profileError?.code !== 'PGRST116') {
        console.error('Error loading profile:', profileError);
        // Still return auth data even if profile fetch fails
        return { ...data, profile: null };
      }

      console.log('Sign in complete. User role:', profileData?.role); // Debug log
      
      // Return both auth data and profile
      return { ...data, profile: profileData };
    } catch (err) {
      console.error('Sign in error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
      
      // Ensure state is cleared even if signOut has issues
      setUser(null);
      setProfile(null);
    } catch (err) {
      console.error('Sign out error:', err);
      // Force clear state even on error
      setUser(null);
      setProfile(null);
      throw err;
    }
  };

  const updateProfile = async (updates) => {
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single();

    if (error) throw error;
    setProfile(data);
    return data;
  };

  const resetPassword = async (email) => {
    try {
      const { data, error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location?.origin}/reset-password`,
      });

      if (error) {
        throw new Error(error?.message || 'Failed to send password reset email');
      }

      return data;
    } catch (err) {
      console.error('Password reset error:', err);
      throw err;
    }
  };

  const value = {
    user,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    updateProfile,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}